import React from 'react';
import { Target } from 'lucide-react';
import type { LinkedIndicatorAssessment } from '../services/ielService';
import { IEL_CLASSIFICATION_LABELS } from '../services/ielService';

interface RotinaLinkedIndicatorsPanelProps {
  assessments: LinkedIndicatorAssessment[];
}

/** Indicadores vinculados à rotina — Meta, Atual, Gap. */
export const RotinaLinkedIndicatorsPanel: React.FC<RotinaLinkedIndicatorsPanelProps> = ({
  assessments,
}) => {
  if (assessments.length === 0) return null;

  return (
    <section className="mt-4 rounded-lg border border-[#2F6FED]/20 bg-[#2F6FED]/5 p-3 dark:border-[#2F6FED]/30 dark:bg-[#2F6FED]/10">
      <h3 className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase text-slate-600 dark:text-slate-300">
        <Target size={13} className="text-[#2F6FED]" />
        Indicadores Vinculados
      </h3>
      <div className="space-y-2">
        {assessments.map((a) => (
          <IndicatorCard key={a.key} assessment={a} />
        ))}
      </div>
    </section>
  );
};

function IndicatorCard({ assessment }: { assessment: LinkedIndicatorAssessment }) {
  const { name, target, current, gap, unit, classification, trend } = assessment;
  const gapTone =
    gap == null ? 'text-slate-400' : gap >= 0 ? 'text-emerald-600' : 'text-red-600';

  const formatVal = (v: number | null) => {
    if (v == null) return '—';
    return `${v}${unit === '%' || !unit ? '%' : ` ${unit}`}`;
  };

  return (
    <div className="rounded-md border border-white/60 bg-white/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-900/50">
      <div className="flex items-start justify-between gap-2">
        <p className="text-[12px] font-bold text-slate-800 dark:text-slate-100">{name}</p>
        {classification && (
          <span className="shrink-0 rounded px-1.5 py-0.5 text-[8px] font-bold uppercase text-[#2F6FED]">
            {IEL_CLASSIFICATION_LABELS[classification]}
          </span>
        )}
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-center">
        <Stat label="Meta" value={formatVal(target)} />
        <Stat label="Atual" value={formatVal(current)} />
        <Stat
          label="Gap"
          value={
            gap != null
              ? `${gap > 0 ? '+' : ''}${gap}${unit === '%' || !unit ? '%' : ` ${unit}`}`
              : '—'
          }
          valueClass={gapTone}
        />
      </div>
      {trend != null && Math.abs(trend) >= 1 && (
        <p className="mt-1.5 text-[9px] text-slate-400">
          Tendência: {trend > 0 ? '+' : ''}
          {trend} p.p. vs. período anterior
        </p>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div>
      <p className="text-[8px] font-semibold uppercase text-slate-400">{label}</p>
      <p className={`text-[12px] font-bold tabular-nums ${valueClass ?? 'text-slate-800 dark:text-slate-100'}`}>
        {value}
      </p>
    </div>
  );
}
